import React, {useState, useEffect} from "react";
import {
  withLocalForm,
  FormGroup,
  onMount,
  DatePicker,
  Page,
  NumberField,
  required,
  withReload,
  SubmitButton,
} from "components/common";
import { withXeroErrors } from "components";
import { compose, withHandlers } from "recompose";
import { Control, actions } from "react-redux-form";
import BackButton from "components/refactoring/BackButton";
import {
  Row,
  Col,
  UncontrolledButtonDropdown,
  DropdownToggle,
  DropdownItem,
  DropdownMenu,
} from "reactstrap";
import { Trans } from "react-i18next";
import Select from "react-select";
import moment from "moment";
import { withOrganisationContextConsumer } from "contexts/OrganisationContext";
import deepPick from "deepPick";
import { NotificationManager } from "react-notifications";

const TrackingSelect = ({ trackingCategory }) => {
  const [selectedOption, setSelectedOption] = useState(
    trackingCategory?.SelectedOption ?? null
  );

  return (
    <Select
      labelKey="Name"
      value={selectedOption}
      valueKey="TrackingOptionID"
      onChange={(selectedOption) => {
        setSelectedOption(selectedOption);
      }}
      style={{ width: 150, backgroundColor: "#F4F7FE"}}
      options={trackingCategory?.Options ?? []}
      placeholder="Select..."
    />
  );
};

export const BankTransfer = ({
  LocalForm,
  state,
  accounts,
  onSubmit,
  XeroErrors,
  trackings,
}) => {

const initialAccount = state.FromBankAccount?.Name || "";

const [fromAccount, setFromAccount] = useState(null);
const [toAccount, setToAccount] = useState(null);
const [fromOptions, setFromOptions] = useState([]);
const [toOptions, setToOptions] = useState([]);

useEffect(() => {
  setFromOptions(accounts.filter((option) => option.AccountID !== toAccount?.AccountID && option.Name !== initialAccount));

  setToOptions(accounts.filter((option) => option.AccountID !== fromAccount?.AccountID && option.Name !== initialAccount));
}, [fromAccount, toAccount, accounts, initialAccount]);

const handleFromChange = (selectedOption) => {
  setFromAccount(selectedOption);
  setToOptions(accounts.filter((option) => option.AccountID !== selectedOption?.AccountID && option.Name !== initialAccount));
};

const handleToChange = (selectedOption) => {
  setToAccount(selectedOption);
  setFromOptions(accounts.filter((option) => option.AccountID !== selectedOption?.AccountID && option.Name !== initialAccount));
};

  return (
  <Page title="Transfer Money">
    <BackButton/>
    <XeroErrors />
    <LocalForm 
      onSubmit={onSubmit}
      validators={{
        FromBankAccount: { required },
        ToBankAccount: { required },
        Date: { required },
        Amount: { required },
      }}
    >
      <FormGroup label="From" model=".FromBankAccount" addControlClass={false}>
        <Control.select style={{backgroundColor:'#F4F7FE'}}
          model=".FromBankAccount"
          component={Select}
          options={fromOptions}
          labelKey="Name"
          valueKey="AccountID"
          onChange={handleFromChange}
        />
      </FormGroup>
      <FormGroup label="To" model=".ToBankAccount" addControlClass={false} className="pt-2 pb-2">
        <Control.select style={{backgroundColor:'#F4F7FE'}} 
          model=".ToBankAccount"
          component={Select}
          options={toOptions}
          labelKey="Name"
          valueKey="AccountID"
          onChange={handleToChange}
        />
      </FormGroup>
      <Row>
        <Col>
          <FormGroup label="Date" model=".Date">
            <DatePicker model=".Date" className="form-control" />
          </FormGroup>
        </Col>
        <Col>
        <FormGroup label="Reference" model=".Reference">
            <Control.text model=".Reference" className="form-control" />
          </FormGroup>
        </Col>
        <Col>
          <FormGroup label="Amount" model=".Amount">
            <NumberField model=".Amount" className="form-control" />
          </FormGroup>
        </Col>
      </Row>
      <hr />
      {Object.keys(trackings).length > 0 && (
        <div>
          <p style={{fontSize: "1.3em"}}><Trans>Tracking Categories</Trans></p>
          <div style={{ display: "flex", maxWidth: 300, gap: "10%", marginTop: "1.3em" }}>
            {Object.values(trackings).map((i) => (
              <div key={i.TrackingCategoryID}>
                <label key={i.TrackingCategoryID}>{i.Name}</label>
                <TrackingSelect trackingCategory={i} />
              </div>
            ))}
          </div>
        </div>
      )}
      <hr />
      <div className="d-flex justify-content-end">
        <UncontrolledButtonDropdown>
          <SubmitButton color="success" onClick={onSubmit}>
            Transfer
          </SubmitButton>
          <Control.custom
            model="local"
            caret
            component={DropdownToggle}
            mapProps={{
              disabled: (props) => !props.fieldValue.valid,
            }}
            color="success"
          />
          <DropdownMenu>
            <DropdownItem onClick={onSubmit}>
              <Trans>Transfer</Trans>
            </DropdownItem>
            <DropdownItem onClick={() => onSubmit({ addAnother: true })}>
              <Trans>Transfer & add another</Trans>
            </DropdownItem>
          </DropdownMenu>
        </UncontrolledButtonDropdown>
      </div>
    </LocalForm>
  </Page>
)};

export const enhance = compose(
  withOrganisationContextConsumer,
  withReload(),
  withXeroErrors(),
  onMount(({ xero }) => async () => {
    const trackings = await xero
      .get(`trackingcategories`)
      .get("data")
      .then((trackings) => ({
        ...trackings,
      }));
    return {
      trackings,
    };
  }),
  onMount(
    ({ fetchAccounts }) =>
      () =>
        Promise.props({
          accounts: fetchAccounts().filter((i) => i.Type === "BANK"),
        })
  ),
  withLocalForm({
    initialState: ({ accounts }) => ({
      Date: moment().format("YYYY-MM-DD"),
      FromBankAccount: accounts.length ? accounts[0] : null,
      Amount: "0.00",
      Reference: "",
    }),
  }),
  withHandlers({
    onSubmit:
      ({ dispatch, xero, t, state, setXeroErrors, reload, history }) =>
      ({ addAnother } = {}) => {
        dispatch(actions.setPending("local", true));

        const item = deepPick(state, [
          "FromBankAccount.AccountID",
          "ToBankAccount.AccountID",
          "Amount",
          "Date",
          "Reference",
        ]);

        return xero
          .post("banktransfers", {
            ...item,
            Date: moment(item.Date).format("YYYY-MM-DD"),
          })
          .then(() => {
            NotificationManager.success(t("Bank transfer successfully added"));

            if (addAnother) {
              reload();
            } else {
              history.push(history.resolvePath(".."));
            }
          }, setXeroErrors)
          .finally(() => dispatch(actions.setPending("local", false)));
      },
  })
);

export default enhance(BankTransfer);
